import { env } from '../config/environment';
const EDGE_FN_URL = env.edgeFnUrl;

class AuthError extends Error {
  constructor() {
    super("Unauthorized");
    this.name = "AuthError";
  }
}

async function adminFetch<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const password = sessionStorage.getItem("admin_password");
  if (!password) throw new AuthError();

  const res = await fetch(EDGE_FN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${password}`,
    },
    body: JSON.stringify({ action, ...params }),
  });

  if (res.status === 401) {
    sessionStorage.removeItem("admin_password");
    throw new AuthError();
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status})`);
  return data as T;
}

// --- Types ---

export interface UserSummary {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  display_name: string;
  credits: number;
  used: number;
  is_beta: boolean;
  total_spent: number;
  /** False when the user has no `ai_token_balances` row (never got welcome credits). */
  has_balance_row: boolean;
}

export interface UserDetail {
  id: string;
  email: string;
  created_at: string;
  display_name: string;
  credits: number;
  used: number;
  is_beta: boolean;
  last_sign_in_at: string | null;
}

export interface Purchase {
  id: string;
  pack_id: string;
  tokens_purchased: number;
  amount_cents: number;
  currency: string;
  status: string;
  source: string | null;
  created_at: string;
}

export interface Generation {
  id: string;
  type: string;
  provider: string;
  token_cost: number;
  status: string;
  prompt: string;
  error: string | null;
  created_at: string;
}

export interface CreditLogEntry {
  id: string;
  amount: number;
  reason: string;
  granted_at: string;
}

export interface DashboardStats {
  total_users: number;
  active_users_7d: number;
  total_revenue_cents: number;
  credits_purchased: number;
  credits_used: number;
  total_generations: number;
  generations_by_type: Record<string, number>;
  /** Real users with no ai_token_balances row — welcome-credit grant never fired. */
  missing_welcome_credits: number;
}

export interface TimeSeriesPoint {
  date: string;
  value: number;
}

export interface GenerationStats {
  by_type: Record<string, Record<string, number>>;
  by_provider: Record<string, number>;
  by_source_env: Record<string, number>;
  daily_series: TimeSeriesPoint[];
  /** Exact number of ai_jobs rows in the range (all statuses). */
  total_jobs?: number;
}

export interface RevenueStats {
  by_pack: Record<string, { count: number; revenue_cents: number }>;
  by_source: Record<string, { count: number; revenue_cents: number }>;
}

export interface UserNote {
  id: string;
  note: string;
  created_at: string;
}

export interface RewardEvent {
  id: string;
  event_type: string;
  credits_awarded: number;
  related_user_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface ReferralInfo {
  code: string | null;
  referred_by_user: { id: string; email: string; display_name: string } | null;
  referrals: { id: string; email: string; display_name: string; created_at: string }[];
}

export interface UserListFilters {
  has_purchased?: boolean;
  zero_balance?: boolean;
  is_beta?: boolean;
  inactive_30d?: boolean;
  missing_welcome_credits?: boolean;
  search?: string;
}

export interface CreditEconomyStats {
  burn_rate_series: TimeSeriesPoint[];
  balance_distribution: Record<string, number>;
  conversion_after_zero_pct: number | null;
  zero_balance_count: number;
  converted_count: number;
}

export interface RewardStatsData {
  by_type_weekly: { week: string; event_type: string; count: number; credits: number }[];
  credits_rewards_vs_purchased: { rewards: number; purchased: number };
  top_referrers: { user_id: string; email: string; count: number }[];
  total_reward_events: number;
}

export interface PowerUser {
  user_id: string;
  email: string;
  display_name: string;
  value: number;
}

export interface PowerUsersData {
  by_generations: PowerUser[];
  by_spend: PowerUser[];
  by_shares: PowerUser[];
  by_referrals: PowerUser[];
}

export interface PlatformStats {
  review_sessions: number;
  team_projects: number;
  team_projects_ready: number;
  published_exports: number;
}

export interface FunnelCohort {
  week: string;
  signups: number;
  first_gen: number;
  second_gen: number;
  first_purchase: number;
}

export interface EngagementStats {
  funnel: FunnelCohort[];
  wau_series: TimeSeriesPoint[];
  /** Total completed purchases in the range (rows in token_purchases). */
  purchases_in_range: number;
  /** Distinct buyers in the range (regardless of when they signed up). */
  distinct_buyers_in_range: number;
}

export interface MissingWelcomeCredits {
  total: number;
  last_30d: number;
}

export const ENTITLEMENTS = ["trial", "lifetime", "yearly", "monthly", "free"] as const;
export type Entitlement = (typeof ENTITLEMENTS)[number];

export interface FunnelStats {
  counts: Record<string, number>;
  counts_new_users: Record<string, number>;
  range_days: number;
  /** The filter that was applied, or null for all entitlements. */
  entitlement: Entitlement | null;
  /** Distinct devices that opened a project, per entitlement; "unknown" = events from
   *  builds that predate the field (everything before 3.6.0) or that fired before the App
   *  Store receipt had loaded. OPTIONAL because the admin UI can be deployed ahead of the
   *  admin-api that added it; the page must not crash on its absence. */
  by_entitlement?: Record<string, number>;
  /** Where PACK PURCHASED came from: token_purchases (authoritative, unfiltered) or the
   *  client-emitted pack_purchased event (only source that can be filtered by entitlement).
   *  Optional for the same deploy-order reason. */
  pack_purchased_source?: "token_purchases" | "events";
  total_event_rows: number;
}

export interface TelemetryInsights {
  depletion_daily: Array<{ date: string; timeline: number; preview: number; ai_panel: number }>;
  project_source: { sample: number; user_project: number; imported: number };
}

export interface ActivityEvent {
  id: string;
  type: "signup" | "purchase" | "generation_failure" | "reward";
  user_id: string;
  user_email: string;
  description: string;
  created_at: string;
  metadata?: Record<string, unknown>;
}

export interface DeployRow {
  id: string;
  deployed_at: string;
  deployed_by: string;
  actions: string[];
  source_changeset: string;
  result: 'success' | 'partial' | 'failed';
  notes: string | null;
}

export interface AllowlistEntry {
  user_id: string;
  note: string | null;
  added_at: string;
}

export interface PhaseCounts {
  created?: number;
  skipped?: number;
  copied?: number;
  failed?: number;
}

export interface RefreshRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  current_phase: string;
  status: string;
  error_message: string | null;
  row_counts: {
    ghost_auth?: PhaseCounts;
    allowlisted_auth?: PhaseCounts;
    storage?: PhaseCounts;
  } | null;
}

// --- API Methods ---

export async function getUser(userId: string) {
  return adminFetch<{
    user: UserDetail;
    purchases: Purchase[];
    generations: Generation[];
    credit_log: CreditLogEntry[];
    notes: UserNote[];
    reward_events: RewardEvent[];
    referral_info: ReferralInfo;
  }>("get-user", { user_id: userId });
}

export async function listUsers(
  offset: number, limit: number, sortBy: string, sortDir: string, filters: UserListFilters,
) {
  return adminFetch<{ users: UserSummary[]; total: number }>(
    "list-users", { offset, limit, sort_by: sortBy, sort_dir: sortDir, filters },
  );
}

export async function addNote(userId: string, note: string) {
  return adminFetch<{ success: boolean; note: UserNote }>("add-note", { user_id: userId, note });
}

export async function toggleBeta(userId: string) {
  return adminFetch<{ success: boolean; is_beta: boolean }>("toggle-beta", { user_id: userId });
}

export async function creditUser(userId: string, amount: number, reason: string) {
  return adminFetch<{ success: boolean; balance: { available: number; used: number; is_beta: boolean } | null }>(
    "credit-user",
    { user_id: userId, amount, reason }
  );
}

export async function getPowerUsers(limit = 10) {
  return adminFetch<PowerUsersData>("power-users", { limit });
}

export async function getPlatformStats(rangeDays = 30) {
  return adminFetch<PlatformStats>("platform-stats", { range_days: rangeDays });
}

export async function getEngagementStats(rangeDays = 90) {
  return adminFetch<EngagementStats>("engagement-stats", { range_days: rangeDays });
}

export async function getFunnelStats(rangeDays = 30, entitlement: Entitlement | null = null) {
  return adminFetch<FunnelStats>("funnel-stats", { range_days: rangeDays, entitlement });
}

export async function getTelemetryInsights(rangeDays = 30) {
  return adminFetch<TelemetryInsights>("telemetry-insights", { range_days: rangeDays });
}

export async function getCreditEconomy(rangeDays = 30) {
  return adminFetch<CreditEconomyStats>("credit-economy", { range_days: rangeDays });
}

export async function getRewardStats(rangeDays = 30) {
  return adminFetch<RewardStatsData>("reward-stats", { range_days: rangeDays });
}

export async function getRecentActivity(offset = 0, limit = 50, eventTypes?: string[]) {
  return adminFetch<{ events: ActivityEvent[]; has_more: boolean }>(
    "recent-activity", { offset, limit, event_types: eventTypes },
  );
}

export async function getDashboardStats() {
  return adminFetch<{ stats: DashboardStats }>("dashboard-stats");
}

export async function getMissingWelcomeCredits() {
  return adminFetch<MissingWelcomeCredits>("missing-welcome-credits");
}

export async function getTimeSeries(metric: string, rangeDays = 30) {
  return adminFetch<{ series: TimeSeriesPoint[] }>("time-series", { metric, range_days: rangeDays });
}

export async function getGenerationStats(rangeDays = 30) {
  return adminFetch<GenerationStats>("generation-stats", { range_days: rangeDays });
}

export async function getRevenueStats(rangeDays = 30) {
  return adminFetch<RevenueStats>("revenue-stats", { range_days: rangeDays });
}

export async function getDeployLog() {
  return adminFetch<{ rows: DeployRow[] }>("list-deploy-log");
}

export async function listAllowlist(): Promise<{ rows: AllowlistEntry[] }> {
  return adminFetch<{ rows: AllowlistEntry[] }>("list-allowlist");
}

export async function addAllowlist(user_id: string, note: string): Promise<void> {
  await adminFetch<{ ok: boolean }>("add-allowlist", { user_id, note });
}

export async function removeAllowlist(user_id: string): Promise<void> {
  await adminFetch<{ ok: boolean }>("remove-allowlist", { user_id });
}

export async function listRefreshRuns(): Promise<{ rows: RefreshRun[] }> {
  return adminFetch<{ rows: RefreshRun[] }>("list-refresh-runs");
}

export async function startDevRefresh(): Promise<{ runId: string; currentPhase: string }> {
  return adminFetch<{ runId: string; currentPhase: string }>("start-dev-refresh");
}

export async function continueDevRefresh(runId: string): Promise<{ runId: string; currentPhase: string; status: string }> {
  return adminFetch<{ runId: string; currentPhase: string; status: string }>("continue-dev-refresh", { runId });
}

export async function testAuth(): Promise<boolean> {
  try {
    await getDashboardStats();
    return true;
  } catch {
    return false;
  }
}

export { AuthError };
